import {SafeHtml} from '@angular/platform-browser';
import Decimal from 'decimal.js';
import * as _ from 'lodash-es';
import {CachedNumberFormat} from '../../common/cached-number-format';
import {
    classForDisplayDecimalZeros,
    classForDisplayDecimalZerosFromDecimal,
    createNumberFormat,
    parseClassForDisplayDecimalZeros
} from '../../common/formatters';
import appConfig from '../../config/app-config';
import {Security} from '../../models/security';
import NumberFormat = Intl.NumberFormat;

export interface SymbolDecimalPlaces {
    quantity?: number;
    report?: number;
    price?: number;
}

export interface SymbolDecimalPlacesWithStore extends SymbolDecimalPlaces {
    store: SymbolDecimalPlaces;
}

export interface DisplayDecimal<T = string | SafeHtml> {
    value: T;
    class: string;
    originalValue?: number;
    withDecimal?: string;
}

export interface DisplayDecimalWithCurrency<T = string | SafeHtml> extends DisplayDecimal<T> {
    currencySymbol: string;
}

export type DisplayDecimalWithOptCurrency<T = string | SafeHtml> = Omit<DisplayDecimalWithCurrency<T>, 'currencySymbol'>
    & Partial<DisplayDecimalWithCurrency<T>>;

export type ValueFormatter = (value: number) => DisplayDecimal;
export type ValueFormatterWithCurrency = (value: number) => DisplayDecimalWithCurrency;
export type ValueFormatterWithOptCurrency = (value: number) => DisplayDecimalWithOptCurrency;

export const EMPTY_DISPLAY_DECIMAL: DisplayDecimal = {class: '', value: ''};

const PERCENT_MULTIPLIER = 100;
const PERCENT_FORMAT = createNumberFormat({
    isDecimalRequired: true,
    decimalCount:      2
});

export const USE_QTY_STORE_FORMAT = true;

export interface SymbolFormats {
    /**
     * quantityFormat: NumberFormat
     * Number of decimals defined by symbol
     * with trim ending zeros
     * example usage quantityFormat.format(7599.8560)
     * output: 7,599.856
     */
    quantityFormat?: NumberFormat;

    /**
     * storeQuantityFormat: NumberFormat
     * Similar to {@link quantityFormat}, but optionally allows some extra decimal places
     * up to {@link Security.qtyScale}, while {@link quantityFormat} prefers {@link Security.qtyViewScale}.
     */
    quantityStoreFormat?: NumberFormat;

    /**
     * reportFormat: NumberFormat
     * Number of decimals defined by symbol
     * with trim ending zeros
     * example usage reportFormat.format(7599.8560)
     * output: 7,599.856
     */
    reportFormat?: NumberFormat;

    /**
     * priceFormat: NumberFormat
     * Number of decimals defined by symbol
     * with trim ending zeros
     * example usage priceFormat.format(7599.8560)
     * output: 7,599.856
     */
    priceFormat?: NumberFormat;

    /**
     * quantityWithDecimalFormat: NumberFormat
     * Number of decimals defined by symbol
     * without trim ending zeros
     * example usage quantityWithDecimalFormat.format(7599.8560)
     * output: 7,599.8560
     */
    quantityWithDecimalFormat?: NumberFormat;

    /**
     * reportWithDecimalFormat: NumberFormat
     * Number of decimals defined by symbol
     * without trim ending zeros
     * example usage reportWithDecimalFormat.format(7599.8560)
     * output: 7,599.8560
     */
    reportWithDecimalFormat?: NumberFormat;

    /**
     * priceWithDecimalFormat: NumberFormat
     * Number of decimals defined by symbol
     * without trim ending zeros
     * example usage priceWithDecimalFormat.format(7599.8560)
     * output: 7,599.8560
     */
    priceWithDecimalFormat?: NumberFormat;

    decimalPlaces: SymbolDecimalPlaces;

    precision: SymbolDecimalPlaces;
}

export interface SymbolDecimalsInputParams {
    decimals: SymbolDecimalPlacesWithStore;
    steps: SymbolDecimalPlacesWithStore;
    min: {
        price: number;
        quantity: number;
        notional?: number;
    };
    max: {
        price: number;
        quantity: number;
    };
    precision: SymbolDecimalPlaces;
}

/**
 * Handle negative scales in security.
 * Scale can be negative, which is multiplier as 10e-(scale).
 * But decimal count cannot be negative, since it's a count of decimal places,
 * and it's used in {@link Decimal} and {@link Intl.NumberFormat}, where
 * negative is not acceptable.
 */
export function scaleToDecimalPlaces(scale: number): number {
    return scale === undefined || scale === null
        ? scale
        : Math.max(0, scale);
}

export function getSymbolDecimalPlaces(security: Security): SymbolDecimalPlacesWithStore {
    const storeDecimalPlaces = getSymbolStoreDecimalPlaces(security);
    const viewDecimalPlaces = getSymbolViewDecimalPlaces(security);

    const chooseDecimalForField = (field: keyof SymbolDecimalPlaces): number =>
        viewDecimalPlaces[field] !== undefined && viewDecimalPlaces[field] !== null
            ? viewDecimalPlaces[field]
            : storeDecimalPlaces[field];

    return {
        quantity: chooseDecimalForField('quantity'),
        report:   chooseDecimalForField('report'),
        price:    chooseDecimalForField('price'),
        store:    storeDecimalPlaces
    };
}

/**
 * It's used in case of unknown securities
 * to format numbers with decimal digits, only when it's needed,
 * but no trailing decimal zeroes.
 */
export function getMaxPrecisionSymbolDecimalPlaces(): SymbolDecimalPlacesWithStore {
    const MIN_PRECISION_DECIMAL_PLACES = 0;
    const MAX_PRECISION_DECIMAL_PLACES = 12;

    return {
        quantity: MIN_PRECISION_DECIMAL_PLACES,
        report:   MIN_PRECISION_DECIMAL_PLACES,
        price:    MIN_PRECISION_DECIMAL_PLACES,
        store:    {
            quantity: MAX_PRECISION_DECIMAL_PLACES,
            report:   MAX_PRECISION_DECIMAL_PLACES,
            price:    MAX_PRECISION_DECIMAL_PLACES
        }
    };
}

export function getSymbolDecimalSteps(security: Security): SymbolDecimalPlacesWithStore {
    const {
              priceStep,
              qtyStep,
              priceScale,
              priceViewScale,
              qtyScale,
              qtyViewScale,
              reportScale,
              reportViewScale
          } = security || {} as Security;

    const chooseScale = (
        scale: number,
        viewScale: number,
        defaultScale: number
    ): number => (viewScale !== undefined && viewScale !== null)
        ? viewScale
        : (scale !== undefined && scale !== null)
            ? scale
            : defaultScale;

    const getStepByScale = (scale: number) => Number(`1e${-scale}`);

    const chooseStep = (stepFieldValue: number, scale: number) =>
        stepFieldValue
            ? stepFieldValue
            : getStepByScale(scale);

    return {
        quantity: chooseStep(qtyStep, chooseScale(qtyScale, qtyViewScale, appConfig.defaultDecimalPlaces.quantity)),
        price:    chooseStep(priceStep, chooseScale(priceScale, priceViewScale, appConfig.defaultDecimalPlaces.price)),
        report:   getStepByScale(chooseScale(reportScale, reportViewScale, appConfig.defaultDecimalPlaces.report)),
        store:    {
            price:    getStepByScale(chooseScale(priceScale, undefined, appConfig.defaultDecimalPlaces.price)),
            quantity: getStepByScale(chooseScale(qtyScale, undefined, appConfig.defaultDecimalPlaces.quantity)),
            report:   getStepByScale(chooseScale(reportScale, undefined, appConfig.defaultDecimalPlaces.report))
        }
    };
}

export function getSymbolStoreDecimalPlaces(security: Security): SymbolDecimalPlaces {
    const {qtyScale, reportScale, priceScale} = security || {} as Security;

    return {
        quantity: _.defaultTo(scaleToDecimalPlaces(qtyScale), appConfig.defaultDecimalPlaces.quantity),
        report:   _.defaultTo(scaleToDecimalPlaces(reportScale), appConfig.defaultDecimalPlaces.report),
        price:    _.defaultTo(scaleToDecimalPlaces(priceScale), appConfig.defaultDecimalPlaces.price)
    };
}

export function getSymbolViewDecimalPlaces(security: Security): SymbolDecimalPlaces {
    const {qtyViewScale, reportViewScale, priceViewScale} = security || {} as Security;

    return {
        quantity: scaleToDecimalPlaces(qtyViewScale),
        report:   scaleToDecimalPlaces(reportViewScale),
        price:    scaleToDecimalPlaces(priceViewScale)
    };
}

export function getSymbolPrecisions(security: Security): SymbolDecimalPlaces {
    const {qtyPrecision, pricePrecision, reportPrecision} = security || {} as Security;

    return {
        quantity: qtyPrecision,
        report:   reportPrecision || pricePrecision && pricePrecision + 2,
        price:    pricePrecision
    };
}

export function createDisplayDecimal(
    value: number,
    numberFormat: NumberFormat | CachedNumberFormat
): DisplayDecimal<string> {
    const isInvalidValue = !value && value !== 0;

    let decimalZeroesClass = '';

    if (!isInvalidValue) {
        const formatOptions = numberFormat.resolvedOptions();
        const decimalCount = formatOptions.minimumFractionDigits;
        const maxDecimalCount = formatOptions.maximumFractionDigits;
        const precision = formatOptions.maximumSignificantDigits;

        decimalZeroesClass = classForDisplayDecimalZeros(value, decimalCount, precision, maxDecimalCount);

        if (decimalCount) {
            numberFormat = CachedNumberFormat.createOrGetFromCache(formatOptions.locale, {
                ...formatOptions,
                minimumFractionDigits: 0
            });
        }
    }

    const displayValue: string = isInvalidValue ? '' : numberFormat.format(value);

    return {
        value: displayValue,
        class: decimalZeroesClass,
        originalValue: value
    };
}

/**
 * @deprecated Use {@link createDisplayDecimalFromBigint} instead as more performant.
 */
export function createBigIntDisplayDecimal(
    value: number | bigint,
    numberFormat: NumberFormat | CachedNumberFormat
): DisplayDecimal<string> {
    const isInvalidValue = !value && value !== 0 && value !== 0n;

    let decimalValue: Decimal;
    let decimalZeroesClass = '';

    if (!isInvalidValue) {
        const formatOptions = numberFormat.resolvedOptions();
        const decimalCount = formatOptions.minimumFractionDigits;
        const maxDecimalCount = formatOptions.maximumFractionDigits;
        const precision = formatOptions.maximumSignificantDigits;

        decimalValue = new Decimal(value);

        if (typeof value === 'bigint' && maxDecimalCount) {
            decimalValue = decimalValue.div(Math.pow(10, decimalCount));
        }

        decimalZeroesClass = classForDisplayDecimalZerosFromDecimal(
            decimalValue,
            decimalCount,
            precision,
            maxDecimalCount
        );

        if (decimalCount) {
            numberFormat = CachedNumberFormat.createOrGetFromCache(formatOptions.locale, {
                ...formatOptions,
                minimumFractionDigits: 0
            });
        }
    }

    const displayValue: string = isInvalidValue ? '' : numberFormat.format(decimalValue.toNumber());

    return {
        value: displayValue,
        class: decimalZeroesClass,
        originalValue: Number(value)
    };
}

export const getEmptyDisplayDecimalAsDash = (): DisplayDecimal<string> => ({
    value: '—',
    class: ''
});

export const dashDisplayDecimalIfEmpty = (displayDecimal: DisplayDecimal): DisplayDecimal =>
    displayDecimal && displayDecimal.value
        ? displayDecimal
        : getEmptyDisplayDecimalAsDash();

export function createDisplayDecimalWithCurrency(
    value: number,
    numberFormat: NumberFormat | CachedNumberFormat,
    currencySymbol: string
): DisplayDecimalWithCurrency<string> {
    const result = createDisplayDecimal(value, numberFormat) as DisplayDecimalWithCurrency<string>;

    result.currencySymbol = (value || value === 0)
        ? currencySymbol
        : '';

    return result;
}

export function formatReport(value: number, symbolFormats: SymbolFormats): DisplayDecimal<string> {
    return createDisplayDecimal(value, symbolFormats.reportFormat);
}

export function formatPrice(value: number, symbolFormats: SymbolFormats): DisplayDecimal<string> {
    return createDisplayDecimal(value, symbolFormats.priceFormat);
}

export function formatQuantity(
    value: number,
    symbolFormats: SymbolFormats,
    useQtyStoreFormat: boolean = !USE_QTY_STORE_FORMAT
): DisplayDecimal<string> {
    return createDisplayDecimal(
        value,
        useQtyStoreFormat ? symbolFormats.quantityStoreFormat : symbolFormats.quantityFormat
    );
}

export function formatQuantityWithCurrency(
    value: number,
    currencySymbol: string,
    symbolFormats: SymbolFormats,
    useQtyStoreFormat: boolean = !USE_QTY_STORE_FORMAT
): DisplayDecimalWithCurrency<string> {
    return createDisplayDecimalWithCurrency(
        value,
        useQtyStoreFormat ? symbolFormats.quantityStoreFormat : symbolFormats.quantityFormat,
        currencySymbol
    );
}

export function formatQtyPxReportWithCurrency(
    value: number,
    currencySymbol: string,
    formatter: (value: number, symbolFormats?: SymbolFormats, useQtyStoreFormat?: boolean) => DisplayDecimal<string>,
    symbolFormats?: SymbolFormats,
    useQtyStoreFormat: boolean = !USE_QTY_STORE_FORMAT
): DisplayDecimalWithCurrency<string> {
    const result = formatter(value, symbolFormats, useQtyStoreFormat) as DisplayDecimalWithCurrency<string>;

    result.currencySymbol = (value || value === 0)
        ? currencySymbol
        : '';

    return result;
}

export function addCurrencyToDisplayDecimal<T = string | SafeHtml>(
    displayDecimal: DisplayDecimalWithOptCurrency<T>,
    currencySymbol: string
): DisplayDecimalWithCurrency<T> {
    displayDecimal.currencySymbol = (displayDecimal.value || displayDecimal.class)
        ? currencySymbol
        : '';

    return displayDecimal as DisplayDecimalWithCurrency<T>;
}

export function formatQtyWithCurrency(
    value: number,
    currencySymbol: string,
    symbolFormats: SymbolFormats,
    useQtyStoreFormat: boolean = !USE_QTY_STORE_FORMAT
): DisplayDecimalWithCurrency<string> {
    return formatQtyPxReportWithCurrency(
        value,
        currencySymbol,
        formatQuantity,
        symbolFormats,
        useQtyStoreFormat
    );
}

export function formatPercents(value: number): DisplayDecimal<string> {
    const percent = !_.isFinite(value) ? null : new Decimal(value).mul(PERCENT_MULTIPLIER).toNumber();

    return createDisplayDecimal(percent, PERCENT_FORMAT);
}

export function formatPercentsNoMult(value: number): DisplayDecimal<string> {
    const percent = !_.isFinite(value) ? null : value;

    return createDisplayDecimal(percent, PERCENT_FORMAT);
}

// tslint:disable:cyclomatic-complexity
export function formatDisplayDecimalAsString(
    displayDecimal: DisplayDecimalWithOptCurrency,
    options?: {
        noSpaceBeforeCurrency: boolean;
    }
): string {
    let result: string = '';

    if (displayDecimal) {
        const resultParts: string[] = [];

        if (_.isString(displayDecimal.withDecimal)) {
            resultParts.push(displayDecimal.withDecimal);
        } else if (_.isString(displayDecimal.value)) {
            resultParts.push(displayDecimal.value);
        }

        if (resultParts.length && displayDecimal.class) {
            const parsedClass = parseClassForDisplayDecimalZeros(displayDecimal.class);

            if (parsedClass.isDecimalPointPresent) {
                resultParts.push('.');
            }

            if (parsedClass.trailingDecimalZeroCount) {
                _.times(parsedClass.trailingDecimalZeroCount, () => resultParts.push('0'));
            }
        }

        if (resultParts.length && displayDecimal.currencySymbol) {
            if (!(options && options.noSpaceBeforeCurrency)) {
                resultParts.push(' ');
            }

            resultParts.push(displayDecimal.currencySymbol);
        }

        result = resultParts.join('');
    }

    return result;
}
